/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'
import { FormattedMessage, useIntl } from 'react-intl'

import { ImgSanity } from '@slite/components'

import handDrawnBorderOrange from '../../assets/hand-drawn-border-orange.svg'
import { Button } from '../../components/Button/Button'
import { Section } from '../../components/Section'
import { TextLayout } from '../../components/TextLayout'
import { useRoutes } from '../../routes'
import { SMALL_BREAKPOINT } from '../../utils/breakpoints'
import type { McpWhySuperCard } from './data'
import {
  getMcpWhySuperCards,
  getMcpWhySuperContent,
  getMcpWhySuperHighlights,
  MCP_TOOLS,
} from './data'

const WHY_MCP_LEARN_SLUG = 'claude-code-company-brain'

const BentoText = ({
  title,
  description,
  titleMaxWidth,
  descriptionMaxWidth,
}: {
  title: string
  titleMaxWidth?: number
  description: string
  descriptionMaxWidth?: number
}) => (
  <div
    css={css`
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 48px;

      @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
        padding: 24px;
      }
    `}
  >
    <p
      className="font-family-title font-weight-medium line-height-normal padding-bottom-4 flexbox flexbox-align-center flexbox-gap-8 shade-00"
      style={{
        fontSize: 28,
        maxWidth: titleMaxWidth ?? 'none',
      }}
    >
      {title}
    </p>
    <p className="shade-10" style={{ maxWidth: descriptionMaxWidth ?? 'none' }}>
      {description}
    </p>
  </div>
)

const BentoIllustration = ({ image }: { image: McpWhySuperCard['image'] }) => (
  <ImgSanity
    src={image.src}
    alt={image.alt}
    width={620}
    css={css`
      display: block;
      object-fit: cover;
      object-position: ${image.objectPosition};
      width: 100%;
      height: 100%;

      @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
        height: auto;
      }
    `}
  />
)

export function SectionMcpWhySuper() {
  const intl = useIntl()
  const routes = useRoutes()

  const content = getMcpWhySuperContent(intl)
  const [featuredCard, ...smallCards] = getMcpWhySuperCards(intl)
  const highlights = getMcpWhySuperHighlights(intl)

  return (
    <Section
      containerVerticalPadding={80}
      background="moon-dust-lighter"
      className="position-relative"
    >
      <div
        css={css`
          padding: 0 24px;

          @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
            padding: 0;
          }
        `}
      >
        <TextLayout
          type="default"
          title={content.title}
          description={content.description}
          descriptionMaxWidth={450}
          titleMaxWidth={600}
          button={
            <Button
              variant="primary"
              size="default"
              href={routes.learn.detail({ slug: WHY_MCP_LEARN_SLUG })}
            >
              <FormattedMessage
                id="mcp.whysuper.blog.button"
                defaultMessage="Read blog post"
              />
            </Button>
          }
        />
      </div>
      <div className="margin-top-24">
        <div className="flexbox flexbox-column flexbox-gap-24">
          <div
            css={css`
              background: var(--shade-100);
              border: 1px solid rgba(0, 0, 0, 0.05);
              height: 500px;
              width: 100%;
              border-radius: 24px;
              overflow: hidden;

              @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
                height: auto;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                width: 100%;
                gap: 24px;

                @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
                  flex-direction: column;
                }
              `}
            >
              <div
                className="flexbox flexbox-column flexbox-gap-8 flexbox-space-between"
                style={{ flex: 1 }}
              >
                <BentoText
                  title={featuredCard.title}
                  titleMaxWidth={300}
                  description={featuredCard.description}
                  descriptionMaxWidth={360}
                />
                <div
                  css={css`
                    padding: 0 12px 48px 48px;
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px 4px;

                    @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
                      padding: 0 20px 24px 24px;
                    }
                  `}
                >
                  {MCP_TOOLS.map((tool) => (
                    <div
                      key={tool.label}
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        padding: 3px 12px 3px 8px;
                        border-image: url('${handDrawnBorderOrange}') 20 /
                          20px / 0px;
                        border-radius: 100px;
                        color: var(--saturn-orange);

                        @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
                          gap: 4px;
                          padding: 1px 6px 1px 6px;

                          svg {
                            width: 12px;
                            height: 12px;
                          }
                        }
                      `}
                    >
                      <tool.Icon size={16} />
                      <span
                        css={css`
                          color: var(--saturn-orange);
                          font-size: 16px;

                          @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
                            font-size: 14px;
                          }
                        `}
                      >
                        {tool.label}
                      </span>
                    </div>
                  ))}
                  <span
                    css={css`
                      font-size: 16px;
                      padding: 4px 8px;
                      color: var(--saturn-orange);

                      @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
                        font-size: 14px;
                      }
                    `}
                  >
                    {intl.formatMessage({
                      id: 'mcp.whysuper.tools.more',
                      defaultMessage: '+27 more tools',
                    })}
                  </span>
                </div>
              </div>
              <div
                css={css`
                  height: 500px;
                  flex: 1;

                  @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
                    height: auto;
                  }
                `}
              >
                <BentoIllustration image={featuredCard.image} />
              </div>
            </div>
          </div>
          <div
            css={css`
              display: flex;
              gap: 24px;
              width: 100%;

              @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
                flex-direction: column;
              }
            `}
          >
            {smallCards.map((card) => (
              <div
                key={card.title}
                className="flexbox flexbox-column flexbox-space-between overflow-hidden background-shade-100"
                style={{
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  flex: 1,
                  borderRadius: 24,
                }}
              >
                <BentoText
                  title={card.title}
                  description={card.description}
                  descriptionMaxWidth={360}
                />
                <div
                  css={css`
                    height: 300px;
                    width: 100%;
                    background: var(--shade-30);

                    @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
                      height: auto;
                    }
                  `}
                >
                  <BentoIllustration image={card.image} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          css={css`
            display: flex;
            gap: 4px;
            width: 100%;
            padding: 48px;
            margin-top: 24px;

            @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
              flex-direction: column;
              padding: 16px;
            }
          `}
        >
          {highlights.map((highlight, index) => (
            <React.Fragment key={highlight.title}>
              {index !== 0 && (
                <div
                  css={css`
                    padding: 4px 24px;

                    @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
                      padding: 16px 0;
                    }
                  `}
                >
                  <div
                    css={css`
                      height: 100%;
                      width: 1px;
                      background: var(--shade-45);

                      @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
                        height: 1px;
                        width: 100%;
                      }
                    `}
                  />
                </div>
              )}

              <div
                className="flexbox flexbox-column flexbox-gap-8"
                style={{ flex: 1 }}
              >
                <p
                  className="flexbox flexbox-align-center flexbox-gap-12 font-weight-semibold shade-00"
                  style={{ fontSize: 18 }}
                >
                  <highlight.Icon size={20} />
                  <span className="padding-top-2">{highlight.title}</span>
                </p>
                <p className="shade-10">{highlight.description}</p>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </Section>
  )
}
